import React, { FC, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { FrozenSales } from "interface/sales";
import { GrideDateFormatter } from "ui/Date";
import { formatWeight, formatCurrency } from "utils/formatters"

interface FrozenSalesMobileListProps {
	items: FrozenSales[];
	loading: boolean;
	onLoadMore: () => void;
}

type FrozenSalesGroup = {
	groupKey: string;
	todaysDate?: Date;
	totalWeight: number;
	items: FrozenSales[];
};

const FrozenSalesMobileList: FC<FrozenSalesMobileListProps> = ({
	items,
	loading,
	onLoadMore,
}) => {
	const { t } = useTranslation();

	const groupedItems = useMemo<FrozenSalesGroup[]>(() => {
		const groups = new Map<string, FrozenSalesGroup>();

		items.forEach((item) => {
			const groupKey = item.group1 || `${item.fishingGearText || "-"} - ${item.packageTypeText || "-"}`;
			const existing = groups.get(groupKey);

			if (!existing) {
				groups.set(groupKey, {
					groupKey,
					todaysDate: item.todaysDate,
					totalWeight: Number(item.netLiveWeightKg || 0),
					items: [item],
				});
				return;
			}

			existing.items.push(item);
			existing.totalWeight += Number(item.netLiveWeightKg || 0);
			if (!existing.todaysDate && item.todaysDate) {
				existing.todaysDate = item.todaysDate;
			}
		});

		return Array.from(groups.values());
	}, [items]);

	const empty = useMemo(() => !loading && groupedItems.length === 0, [groupedItems.length, loading]);

	useEffect(() => {
		const handleWindowScroll = () => {
			const remaining =
				document.documentElement.scrollHeight -
				window.innerHeight -
				window.scrollY;

			if (remaining < 200) {
				onLoadMore();
			}
		};

		window.addEventListener("scroll", handleWindowScroll, { passive: true });
		handleWindowScroll();

		return () => {
			window.removeEventListener("scroll", handleWindowScroll);
		};
	}, [onLoadMore, items.length, loading]);

	return (
		<div className="frozen-sales-list-mobile">
			{empty && <div className="frozen-sales-list-mobile-empty">{t("NoRows")}</div>}

			{groupedItems.map((group, groupIndex) => (
				<div
					key={`${group.groupKey}-${groupIndex}`}
					className="frozen-sales-list-card"
				>
					<div className="frozen-sales-list-card-header">
						<div className="frozen-sales-list-card-group">{group.groupKey}</div>
						<div className="frozen-sales-list-card-date">&nbsp;</div>
					</div>

					<div className="frozen-sales-list-card-body">
						{group.items.map((item, itemIndex) => (
							<div
								key={`${group.groupKey}-${item.productText || "noproduct"}-${itemIndex}`}
								className="frozen-sales-list-row"
							>
								<div className="frozen-sales-list-row-main">
									<div className="frozen-sales-list-row-title">{item.productText || "-"}</div>
								</div>
								<div className="frozen-sales-list-row-metrics">
									<div>
										<span>{t("Weight")}</span>
										<strong>{formatWeight(item.netLiveWeightKg)}</strong>
									</div>
									<div>
										<span>{t("HighestPrice")}</span>
										<strong>{formatCurrency(item.maxPrice)}</strong>
									</div>
									<div>
										<span>{t("AveragePrice")}</span>
										<strong>{formatCurrency(item.averagePrice)}</strong>
									</div>
								</div>
							</div>
						))}
					</div>

					<div className="frozen-sales-list-card-footer">
						<div>
							<span>{t("Rows")}</span>
							<strong>{group.items.length}</strong>
						</div>
						<div>
							<span>{t("TotalWeight")}</span>
							<strong>{formatWeight(group.totalWeight)}</strong>
						</div>
					</div>
				</div>
			))}

			{loading && <div className="frozen-sales-list-mobile-loading">{t("Loading")}</div>}
		</div>
	);
};

export default FrozenSalesMobileList;